import { queryClient } from '@/lib/queryClient';
import { toQueryString, type QsRecord } from '../lib/qs';

export class PmApiError extends Error {
  status: number;
  code: string;
  body: unknown;
  constructor(status: number, code: string, message?: string, body?: unknown) {
    super(message ?? code);
    this.name = 'PmApiError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export type PmQuery = QsRecord;

export interface PmFetchInit {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: PmQuery;
  body?: unknown;
  signal?: AbortSignal;
}

/**
 * No retry for auth / not-found / conflict / not-deployed; two retries for anything else (network, 5xx incl. the
 * `server_error` an nginx 502/504 HTML page becomes during a PM2 reload).
 */
export function pmRetry(failureCount: number, error: unknown): boolean {
  if (error instanceof PmApiError) {
    if ([401, 403, 404, 409].includes(error.status) || error.code === 'api_missing') return false;
  }
  return failureCount < 2;
}

const API_MISSING_MESSAGE = 'The PM API is not deployed yet';

/** An HTML document (SPA index.html, nginx error page) rather than an API body. */
function looksLikeHtml(text: string): boolean {
  return /^\s*</.test(text);
}

/**
 * Status + body text -> parsed body or PmApiError. Classification of the "not an API answer" cases:
 * - HTML on a 2xx (SPA fallback serves index.html) or a 404 (route not proxied) -> `api_missing` (not retried);
 * - a JSON 404 on `/me` (API up, PM module not registered: Fastify's own "Route not found") -> `api_missing`;
 * - any other non-JSON 5xx (nginx 502/504 while PM2 reloads) -> `server_error` (retried);
 * - any other non-JSON body -> `invalid_response` (2xx) / `http_<status>`.
 */
export function interpretPmResponse<T>(path: string, status: number, text: string): T {
  if (status === 204) return undefined as T;
  let parsed: unknown;
  let isJson = true;
  try {
    parsed = text === '' ? undefined : JSON.parse(text);
  } catch {
    isJson = false;
  }
  const isOk = status >= 200 && status < 300;
  if (status === 401) {
    queryClient.setQueryData(['/api/user'], null);
  }
  if (!isJson) {
    if (looksLikeHtml(text) && (isOk || status === 404)) throw new PmApiError(status, 'api_missing', API_MISSING_MESSAGE);
    if (status >= 500) throw new PmApiError(status, 'server_error', 'The server is temporarily unavailable — please try again in a moment');
    throw new PmApiError(status, isOk ? 'invalid_response' : 'http_' + status, isOk ? 'Unexpected response from the PM API' : undefined);
  }
  if (isOk) return parsed as T;
  if (status === 404 && path === '/me') throw new PmApiError(status, 'api_missing', API_MISSING_MESSAGE, parsed);
  const b = (parsed && typeof parsed === 'object' ? parsed : {}) as { error?: string; message?: string };
  throw new PmApiError(status, b.error ?? 'http_' + status, b.message, parsed);
}

export async function pmFetch<T>(path: string, init: PmFetchInit = {}): Promise<T> {
  const method = init.method ?? 'GET';
  if (import.meta.env.DEV && import.meta.env.VITE_PM_MOCK === '1') {
    const { handleMockRequest } = await import('./mock/mock-adapter');
    const res = await handleMockRequest({ method, path, query: init.query, body: init.body });
    if (res.html) return interpretPmResponse<T>(path, res.status, '<!doctype html><html></html>');
    return interpretPmResponse<T>(path, res.status, res.body === undefined ? '' : JSON.stringify(res.body));
  }
  const url = '/api/pm' + path + toQueryString(init.query);
  const hasBody = init.body !== undefined;
  const res = await fetch(url, {
    method,
    credentials: 'include',
    headers: hasBody ? { 'Content-Type': 'application/json' } : undefined,
    body: hasBody ? JSON.stringify(init.body) : undefined,
    signal: init.signal,
  });
  const text = res.status === 204 ? '' : await res.text();
  return interpretPmResponse<T>(path, res.status, text);
}

export const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

/** `filename` of a Content-Disposition header (quoted or bare, RFC 5987 `filename*=UTF-8''…` preferred); null when absent. */
export function fileNameFromDisposition(header: string | null | undefined): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*(?:[\w-]+)?'[^']*'([^;]+)/i.exec(header);
  if (star) {
    try {
      const v = decodeURIComponent(star[1].trim());
      if (v) return v;
    } catch {
      /* fall through to the plain filename */
    }
  }
  const quoted = /filename\s*=\s*"([^"]*)"/i.exec(header);
  if (quoted) return quoted[1] || null;
  const bare = /filename\s*=\s*([^;]+)/i.exec(header);
  const v = bare?.[1].trim();
  return v ? v : null;
}

/** Binary GET under /api/pm (same URL building + credentials as `pmFetch`); used by the Excel export. */
export async function pmFetchBlob(path: string, init: { query?: PmQuery; signal?: AbortSignal } = {}): Promise<{ blob: Blob; fileName: string | null }> {
  if (import.meta.env.DEV && import.meta.env.VITE_PM_MOCK === '1') {
    throw new PmApiError(501, 'mock_unsupported', 'Excel export is not available in mock mode');
  }
  const url = '/api/pm' + path + toQueryString(init.query);
  const res = await fetch(url, { method: 'GET', credentials: 'include', signal: init.signal });
  if (res.status < 200 || res.status >= 300) {
    interpretPmResponse<unknown>(path, res.status, await res.text());
    throw new PmApiError(res.status, 'http_' + res.status);
  }
  const type = res.headers.get('content-type') ?? '';
  if (!type.toLowerCase().startsWith(XLSX_CONTENT_TYPE)) throw new PmApiError(res.status, 'api_missing', API_MISSING_MESSAGE);
  return { blob: await res.blob(), fileName: fileNameFromDisposition(res.headers.get('content-disposition')) };
}
